import React, { useEffect, useRef } from "react";

interface InfiniteScrollProps {
  loadMore: () => void;
  hasMore: boolean;
  threshold?: number;
  preloadBatch?: boolean;
}

const InfiniteScroll: React.FC<InfiniteScrollProps> = ({
  loadMore = () => {},
  hasMore,
  threshold = 300,
  preloadBatch = false,
}) => {
  const sentinel = useRef<HTMLDivElement>(null);
  const scrollTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Preload first batch if desired
    if (preloadBatch && hasMore) {
      loadMore();
    }
  }, [preloadBatch, hasMore]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && hasMore) {
            // Clear any existing timeout
            if (scrollTimeout.current) {
              clearTimeout(scrollTimeout.current);
            }

            // Set a small delay to prevent too many consecutive calls
            scrollTimeout.current = setTimeout(() => {
              loadMore();
            }, 100);
          }
        });
      },
      {
        rootMargin: `0px 0px ${threshold}px 0px`,
      }
    );

    if (sentinel.current) {
      observer.observe(sentinel.current);
    }

    return () => {
      if (sentinel.current) {
        observer.unobserve(sentinel.current);
      }
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
    };
  }, [loadMore, hasMore, threshold]);

  return <div ref={sentinel} style={{ height: "10px", width: "100%" }} />;
};

export default InfiniteScroll;
